import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
} from 'react';

import { trackProductEvent } from '@/features/analytics';

import type { PublicPracticeSection, PublicPracticeSong } from './types';

interface LoopRange {
  key: string;
  start: number;
  end: number;
}

const MILESTONE_MS = 30_000;
const PLAYBACK_TICK_MS = 250;

export function clampLoopRange(
  startSec: number,
  endSec: number,
  durationSec: number,
): Pick<LoopRange, 'start' | 'end'> {
  const duration = Math.max(0, durationSec);
  const minimumGap = Math.min(0.25, duration);
  const start = Math.max(0, Math.min(startSec, Math.max(0, duration - minimumGap)));
  const end = Math.max(
    start + minimumGap,
    Math.min(endSec, duration),
  );
  return { start, end };
}

function formatTime(value: number): string {
  const safeValue = Number.isFinite(value) ? Math.max(0, value) : 0;
  const minutes = Math.floor(safeValue / 60);
  const seconds = Math.floor(safeValue % 60);
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

function timelineStyle(
  section: PublicPracticeSection,
  durationSec: number,
): CSSProperties {
  const start = Math.max(0, Math.min(100, (section.start_sec / durationSec) * 100));
  const width = Math.max(
    2,
    Math.min(100 - start, ((section.end_sec - section.start_sec) / durationSec) * 100),
  );
  return {
    '--practice-section-start': `${start}%`,
    '--practice-section-width': `${width}%`,
  } as CSSProperties;
}

export default function PublicPracticePreview({
  song,
  onMediaUnavailable,
}: {
  song: PublicPracticeSong;
  onMediaUnavailable: () => void;
}) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [loop, setLoop] = useState<LoopRange | null>(null);
  const playTrackedRef = useRef(false);
  const milestoneTrackedRef = useRef(false);
  const accumulatedPlaybackMsRef = useRef(0);
  const playbackStartedAtRef = useRef<number | null>(null);
  const playbackTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const trackMilestoneIfReady = useCallback(() => {
    if (
      milestoneTrackedRef.current
      || playbackStartedAtRef.current === null
    ) {
      return;
    }
    const elapsed = Date.now() - playbackStartedAtRef.current;
    if (accumulatedPlaybackMsRef.current + elapsed < MILESTONE_MS) {
      return;
    }
    milestoneTrackedRef.current = true;
    trackProductEvent(
      'practice_audio_30s',
      { song_key: song.song_key },
      'practice_song',
    );
  }, [song.song_key]);

  const stopPlaybackClock = useCallback(() => {
    if (playbackStartedAtRef.current !== null) {
      accumulatedPlaybackMsRef.current += Date.now() - playbackStartedAtRef.current;
      playbackStartedAtRef.current = null;
    }
    if (playbackTimerRef.current !== null) {
      clearInterval(playbackTimerRef.current);
      playbackTimerRef.current = null;
    }
  }, []);

  useEffect(() => () => stopPlaybackClock(), [stopPlaybackClock]);

  function handlePlaying() {
    if (playbackStartedAtRef.current === null) {
      playbackStartedAtRef.current = Date.now();
    }
    if (playbackTimerRef.current === null) {
      playbackTimerRef.current = setInterval(
        trackMilestoneIfReady,
        PLAYBACK_TICK_MS,
      );
    }
  }

  function handlePlay() {
    setIsPlaying(true);
    if (!playTrackedRef.current) {
      playTrackedRef.current = true;
      trackProductEvent(
        'practice_preview_play',
        { song_key: song.song_key },
        'practice_song',
      );
    }
  }

  function handlePause() {
    setIsPlaying(false);
    stopPlaybackClock();
  }

  async function togglePlayback() {
    const audio = audioRef.current;
    if (!audio) {
      return;
    }
    if (isPlaying) {
      audio.pause();
      return;
    }
    try {
      await audio.play();
    } catch {
      onMediaUnavailable();
    }
  }

  function selectLoop(section: PublicPracticeSection) {
    const audio = audioRef.current;
    const selected = clampLoopRange(
      section.start_sec,
      section.end_sec,
      song.duration_sec,
    );
    setLoop({ key: section.key, ...selected });
    if (audio) {
      audio.currentTime = selected.start;
      setCurrentTime(selected.start);
    }
    trackProductEvent(
      'practice_loop_used',
      { song_key: song.song_key },
      'practice_song',
    );
  }

  function handleTimeUpdate() {
    const audio = audioRef.current;
    if (!audio) {
      return;
    }
    if (loop && audio.currentTime >= loop.end) {
      audio.currentTime = loop.start;
    }
    setCurrentTime(audio.currentTime);
  }

  function handleSeek(value: string) {
    const audio = audioRef.current;
    if (!audio) {
      return;
    }
    const nextTime = Math.max(
      0,
      Math.min(Number(value), song.duration_sec),
    );
    audio.currentTime = nextTime;
    setCurrentTime(nextTime);
  }

  return (
    <section
      className="public-practice-player"
      aria-labelledby="public-practice-player-title"
      data-playing={isPlaying}
    >
      <div className="public-practice-player__heading">
        <div>
          <p>연습용 MR</p>
          <h2 id="public-practice-player-title">먼저 한 번 듣고, 구간을 고르세요</h2>
        </div>
        <output aria-live="off">
          {formatTime(currentTime)} / {formatTime(song.duration_sec)}
        </output>
      </div>

      <audio
        ref={audioRef}
        data-testid="public-practice-audio"
        preload="metadata"
        src={song.mr_path}
        onPlay={handlePlay}
        onPlaying={handlePlaying}
        onPause={handlePause}
        onWaiting={stopPlaybackClock}
        onEnded={handlePause}
        onError={() => {
          handlePause();
          onMediaUnavailable();
        }}
        onTimeUpdate={handleTimeUpdate}
      >
        브라우저에서 오디오 재생을 지원하지 않습니다.
      </audio>

      <div className="public-practice-transport">
        <button
          type="button"
          className="public-practice-play"
          aria-label={isPlaying ? 'MR 일시정지' : 'MR 재생'}
          onClick={() => void togglePlayback()}
        >
          <span aria-hidden="true">{isPlaying ? 'Ⅱ' : '▶'}</span>
        </button>
        <label className="public-practice-scrubber">
          <span className="sr-only">재생 위치</span>
          <input
            type="range"
            min="0"
            max={song.duration_sec}
            step="0.1"
            value={currentTime}
            aria-label="재생 위치"
            onChange={(event) => handleSeek(event.target.value)}
          />
        </label>
      </div>

      <div
        className="public-practice-timeline"
        role="region"
        aria-label="MR 구간 타임라인"
      >
        <div className="public-practice-timeline__rail" aria-hidden="true">
          {Array.from({ length: 32 }, (_, index) => <i key={index} />)}
        </div>
        <div className="public-practice-timeline__sections">
          {song.sections.map((section) => (
            <button
              key={section.key}
              type="button"
              className={loop?.key === section.key ? 'is-selected' : undefined}
              style={timelineStyle(section, song.duration_sec)}
              aria-label={`${section.label} 반복 구간 선택`}
              aria-pressed={loop?.key === section.key}
              onClick={() => selectLoop(section)}
            >
              <strong>{section.label}</strong>
              <span>
                {formatTime(section.start_sec)}–{formatTime(section.end_sec)}
              </span>
            </button>
          ))}
        </div>
      </div>
    </section>
  );
}
